<template>
  <div>
    <div id="quickquote-step-2-cover">
      <h1 v-html="labelTitle" />
      <section>
        <article class="flow-group">
          <div class="control">
            <section v-if="vueModel.Coverage.ProductInformationBoxText.length > 0">
              <div>
                <p
                  class="info-box is-shown info-box-no-arrow no-margin-top no-margin-bottom"
                  v-html="vueModel.Coverage.ProductInformationBoxText"
                />
              </div>
            </section>

            <!-- CLAUSE SECTION -->
            <section v-if="vueModel.Coverage.Clauses.length > 0" data-test-clause-section>
              <p>
                <a class="round-arrow-with-text" data-collapse="coverageClause">
                  <span class="round-arrow down" />
                  <span v-html="contentModel.SharedContent.LabelClauseNote" />
                </a>
              </p>
              <div id="coverageClause" class="collapse-target">
                <div
                  v-for="(clause, index) in vueModel.Coverage.Clauses"
                  :key="'coverage-clause-' + index"
                >
                  <strong v-html="clause.Title" />
                  <p v-if="clause.Description" v-html="clause.Description" />
                </div>
              </div>
            </section>

            <!-- COVER SECTIONS -->
            <div
              v-for="(coverSection, coverSectionIndex) in vueModel.Coverage.CoverSections"
              :key="'coverage-cover-section-' + coverSectionIndex"
            >
              <section data-test-cover-section>
                <div>
                  <!-- NON COLLAPSABLE COVER SECTION -->
                  <!-- Modified to support both methods in the same div -->
                  <div
                    :class="['row', { 'qq-alternative-design': coverSection.UseAlternativeDesign }]"
                  >
                    <div class="columns">
                      <div>
                        <p
                          v-if="coverSection.RenderingType === 0"
                          :class="[{ 'qq-p-header': coverSection.UseAlternativeDesign }]"
                        >
                          <strong
                            class="h2"
                            data-test-cover-section-title
                            v-html="coverSection.Name"
                          />
                          <tooltip
                            v-if="coverSection.HasTooltip && !coverSection.UseAlternativeDesign"
                            :id="'cover-section-' + coverSectionIndex"
                            :content="coverSection.Tooltip"
                          />
                          <base-info-link-modal
                            v-if="coverSection.InfoLinkModal.HasInfoLinkModal"
                            slot="infolink"
                            :link-label="coverSection.InfoLinkModal.LinkLabel"
                            :title="coverSection.InfoLinkModal.Title"
                            :content="coverSection.InfoLinkModal.Content"
                            :button-label="coverSection.InfoLinkModal.ButtonLabel"
                            :align-right="true"
                          />
                        </p>
                        <p v-if="coverSection.RenderingType === 1">
                          <a
                            class="round-arrow-with-text"
                            :data-collapse="'id' + removeSpecialChars(coverSection.Name)"
                          >
                            <span class="h1 icon-arrow-down-circle down" />
                            <span v-html="coverSection.Name" />
                            <tooltip
                              v-if="coverSection.HasTooltip"
                              :id="'cover-section-' + coverSectionIndex"
                              :content="coverSection.Tooltip"
                            />
                          </a>
                        </p>
                      </div>
                      <div
                        v-for="(cover, index) in coverSection.CoverElements"
                        :id="'id' + removeSpecialChars(coverSection.Name)"
                        :key="'cover-section-cover-element-' + index"
                        :class="{ 'collapse-target': coverSection.RenderingType === 1 }"
                      >
                        <!-- COVER BEGIN -->
                        <!-- NOTEDITABLE -->
                        <div
                          v-if="cover.RenderingType === 0"
                          data-test-cover
                          data-test-cover-type="noteditable"
                        >
                          <p>
                            {{ Utility.decodeHtml(cover.Value) }}
                            <tooltip
                              v-if="cover.HasTooltip"
                              :id="'cover-' + index + '-' + cover.OptionsId"
                              :content="cover.Tooltip"
                            />
                          </p>
                        </div>
                        <!-- STATIC -->
                        <div
                          v-if="cover.RenderingType === 1"
                          data-test-cover
                          data-test-cover-type="static"
                        >
                          <ul class="list-check-icon">
                            <li :data-test="'quick-quote-covers-static-' + index">
                              <span v-if="isStaticCoverWithValue(cover)">
                                {{ cover.Name }}: {{ cover.Value }}&nbsp;
                              </span>
                              <span v-else>{{ cover.Name }}</span>
                              <tooltip
                                v-if="cover.HasTooltip && !coverSection.UseAlternativeDesign"
                                :id="coverSectionIndex + '-cover-' + index + '-' + cover.OptionsId"
                                :content="cover.Tooltip"
                              />
                              <base-info-link-modal
                                v-if="cover.InfoLinkModal.HasInfoLinkModal"
                                slot="infolink"
                                :link-label="cover.InfoLinkModal.LinkLabel"
                                :title="cover.InfoLinkModal.Title"
                                :content="cover.InfoLinkModal.Content"
                                :button-label="cover.InfoLinkModal.ButtonLabel"
                                :align-right="true"
                              />
                            </li>
                          </ul>
                        </div>
                        <!-- SLIDER -->
                        <div
                          v-if="cover.RenderingType === 2"
                          data-test-cover
                          data-test-cover-type="slider"
                        >
                          <div class="columns">
                            <div class="slider-container">
                              <vue-slider
                                ref="slider"
                                v-model="cover.selected"
                                :data="getSlideTexts(cover.Amounts)"
                                :piecewise="true"
                                :tooltip="'always'"
                                :marks="true"
                                :hide-label="true"
                                :data-test="
                                  'quick-quote-covers-slider-' +
                                  cover.GroupId +
                                  '-' +
                                  cover.OptionsId
                                "
                                :data-test-quick-quote-covers-slider-options="
                                  getSlideIds(cover.Amounts)
                                "
                                @click="setSlideValue(cover)"
                                @callback="setSlideValue(cover)"
                                @drag="dragging = true"
                                @drag-end="
                                  dragging = false;
                                  setSlideValue(cover);
                                "
                              />
                            </div>
                          </div>
                        </div>
                        <!-- DROPDOWN -->
                        <div
                          v-if="cover.RenderingType === 3"
                          data-test-cover
                          data-test-cover-type="dropdown"
                        >
                          <label-and-info-link-modal
                            v-if="cover.Name"
                            data-test-cover-dropdown-label-and-info-link-modal
                            :label="cover.Name"
                            :info-link-modal-option="
                              cover.InfoLinkModal.HasInfoLinkModal
                                ? {
                                    linkLabel: cover.InfoLinkModal.LinkLabel,
                                    title: cover.InfoLinkModal.Title,
                                    content: cover.InfoLinkModal.Content,
                                    buttonLabel: cover.InfoLinkModal.ButtonLabel,
                                    alignRight: true
                                  }
                                : null
                            "
                          />
                          <VueSelect
                            :id="'Id'"
                            v-model="cover.selected"
                            :options="cover.Amounts"
                            label="Text"
                            :clearable="false"
                            :data-test-quick-quote-covers-dropdown-options="
                              getSlideIds(cover.Amounts)
                            "
                            @update:modelValue="setValueFromObject(cover)"
                          />
                        </div>
                        <!-- RADIO BUTTONS -->
                        <div
                          v-if="cover.RenderingType === 4"
                          v-setRadioSelcted="cover"
                          data-test-cover
                          data-test-cover-type="radio"
                        >
                          <div
                            v-for="(item, index) in cover.Amounts"
                            :key="'cover-amount-' + index"
                            class="radio-stacked"
                          >
                            <input
                              :id="item.Id + '-' + index"
                              v-model="cover.selected"
                              :rules="'required'"
                              type="radio"
                              :name="cover.Name"
                              :value="item.Text"
                              :data-test="'quick-quote-covers-input-' + cover.GroupId + '-' + index"
                              @change="setValue(cover)"
                            />
                            <label :for="item.Id + '-' + index">{{ item.Text }}</label>
                          </div>
                        </div>
                        <!-- CHECKBOX -->
                        <div
                          v-if="cover.RenderingType === 5"
                          data-test-cover
                          data-test-cover-type="checkbox"
                        >
                          <fieldset class="checkbox-inline">
                            <input
                              :id="cover.Name + index"
                              v-model="cover.IsSelected"
                              type="checkbox"
                              :disabled="
                                coverDisableStatus(cover.GroupId, cover.OptionsId) ||
                                cover.IsEditable == false
                              "
                              :data-test="
                                'quick-quote-covers-input-' + cover.GroupId + '-' + cover.OptionsId
                              "
                              @change="updatePriceCover(cover)"
                            />
                            <LabelAndTooltip
                              v-if="!cover.InfoLinkModal.HasInfoLinkModal"
                              :id="coverSectionIndex + '-cover-' + index + '-' + cover.OptionsId"
                              :for="cover.Name + index"
                              :label="cover.Name"
                              :tooltip="cover.Tooltip"
                            />
                            <label
                              v-if="cover.InfoLinkModal.HasInfoLinkModal"
                              :data-test="
                                coverSectionIndex + '-cover-' + index + '-' + cover.OptionsId
                              "
                              :for="cover.Name + index"
                              v-html="cover.Name"
                            />
                            <base-info-link-modal
                              v-if="cover.InfoLinkModal.HasInfoLinkModal"
                              slot="infolink"
                              :link-label="cover.InfoLinkModal.LinkLabel"
                              :title="cover.InfoLinkModal.Title"
                              :content="cover.InfoLinkModal.Content"
                              :button-label="cover.InfoLinkModal.ButtonLabel"
                              :align-right="false"
                            />
                          </fieldset>
                          <p
                            v-if="cover.DisplayedHelpTextOnSelected !== '' && cover.IsSelected"
                            class="info-box is-open info-box-align-left no-margin-top"
                            v-html="cover.DisplayedHelpTextOnSelected"
                          />
                          <p
                            v-if="cover.DisplayedHelpTextOnDeSelected !== '' && !cover.IsSelected"
                            class="info-box is-open info-box-align-left no-margin-top"
                            v-html="cover.DisplayedHelpTextOnDeSelected"
                          />
                          <p
                            v-if="coverDisableStatus(cover.GroupId, cover.OptionsId)"
                            class="info-box is-open info-box-align-left no-margin-top"
                            v-html="coverDisableReason(cover.GroupId, cover.OptionsId)"
                          />
                          <div
                            v-if="cover.NestedCovers.length > 0"
                            data-test-cover-type="indented-checkbox"
                          >
                            <div
                              v-for="(nestedCover, index) in cover.NestedCovers"
                              :key="'cover-section-nested-cover-element-' + index"
                              class="checkbox-indented"
                            >
                              <fieldset class="checkbox-inline indented-input">
                                <span class="indent-icon" />
                                <input
                                  :id="nestedCover.Name + index"
                                  v-model="nestedCover.IsSelected"
                                  type="checkbox"
                                  :disabled="
                                    coverDisableStatus(
                                      nestedCover.GroupId,
                                      nestedCover.OptionsId
                                    ) || !nestedCover.Id
                                  "
                                  :data-test="
                                    'quick-quote-covers-input-' +
                                    nestedCover.GroupId +
                                    '-' +
                                    nestedCover.OptionsId
                                  "
                                  @change="updatePriceCover(nestedCover)"
                                />
                                <LabelAndTooltip
                                  :id="
                                    coverSectionIndex +
                                    '-nested-cover-' +
                                    index +
                                    '-' +
                                    nestedCover.OptionsId
                                  "
                                  :for="nestedCover.Name + index"
                                  :label="nestedCover.Name"
                                  :tooltip="!nestedCover.Id ? '' : nestedCover.Tooltip"
                                />
                              </fieldset>
                              <div class="indented-warning">
                                <p
                                  v-if="
                                    nestedCover.DisplayedHelpTextOnSelected !== '' &&
                                    nestedCover.IsSelected
                                  "
                                  class="info-box is-open info-box-align-left no-margin-top"
                                  v-html="nestedCover.DisplayedHelpTextOnSelected"
                                />
                                <p
                                  v-if="
                                    nestedCover.DisplayedHelpTextOnDeSelected !== '' &&
                                    !nestedCover.IsSelected
                                  "
                                  class="info-box is-open info-box-align-left no-margin-top"
                                  v-html="nestedCover.DisplayedHelpTextOnDeSelected"
                                />
                                <p
                                  v-if="
                                    coverDisableStatus(nestedCover.GroupId, nestedCover.OptionsId)
                                  "
                                  class="info-box is-open info-box-align-left no-margin-top"
                                  v-html="
                                    coverDisableReason(nestedCover.GroupId, nestedCover.OptionsId)
                                  "
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- CHECKBOX CARD -->
                        <div
                          v-if="cover.RenderingType === 8"
                          data-test-cover
                          data-test-cover-type="checkbox-card"
                        >
                          <base-single-checkbox-card
                            v-model="cover.IsSelected"
                            :option="{
                              label: cover.Name,
                              description: cover.Tooltip
                            }"
                            :disabled="
                              coverDisableStatus(cover.GroupId, cover.OptionsId) ||
                              !cover.IsEditable
                            "
                            :name="cover.Name + index"
                            @input="updatePriceCover(cover)"
                          >
                            <template #infolink>
                              <base-info-link-modal
                                v-if="cover.InfoLinkModal.HasInfoLinkModal"
                                :link-label="cover.InfoLinkModal.LinkLabel"
                                :title="cover.InfoLinkModal.Title"
                                :content="cover.InfoLinkModal.Content"
                                :button-label="cover.InfoLinkModal.ButtonLabel"
                                :align-right="false"
                              />
                            </template>
                          </base-single-checkbox-card>
                          <p
                            v-if="cover.DisplayedHelpTextOnSelected !== '' && cover.IsSelected"
                            class="info-box is-open info-box-align-left no-margin-top"
                            v-html="cover.DisplayedHelpTextOnSelected"
                          />
                          <p
                            v-if="cover.DisplayedHelpTextOnDeSelected !== '' && !cover.IsSelected"
                            class="info-box is-open info-box-align-left no-margin-top"
                            v-html="cover.DisplayedHelpTextOnDeSelected"
                          />
                          <p
                            v-if="coverDisableStatus(cover.GroupId, cover.OptionsId)"
                            class="info-box is-open info-box-align-left no-margin-top"
                            v-html="coverDisableReason(cover.GroupId, cover.OptionsId)"
                          />
                          <div
                            v-if="cover.NestedCovers.length > 0"
                            data-test-cover-type="indented-checkbox-card"
                          >
                            <div
                              v-for="(nestedCover, index) in cover.NestedCovers"
                              :key="'cover-section-nested-cover-element-' + index"
                              class="checkbox-card-nested"
                            >
                              <div v-if="nestedCover !== null">
                                <span class="checkbox-card-indent-icon" />
                                <div class="checkbox-card-nested-input">
                                  <base-single-checkbox-card
                                    v-model="nestedCover.IsSelected"
                                    :option="{
                                      label: nestedCover.Name,
                                      description: nestedCover.Tooltip
                                    }"
                                    :disabled="
                                      coverDisableStatus(
                                        nestedCover.GroupId,
                                        nestedCover.OptionsId
                                      ) || !nestedCover.IsEditable
                                    "
                                    :name="nestedCover.Name + index"
                                    @input="updatePriceCover(nestedCover)"
                                  >
                                    <template #infolink>
                                      <base-info-link-modal
                                        v-if="nestedCover.InfoLinkModal.HasInfoLinkModal"
                                        :link-label="nestedCover.InfoLinkModal.LinkLabel"
                                        :title="nestedCover.InfoLinkModal.Title"
                                        :content="nestedCover.InfoLinkModal.Content"
                                        :button-label="nestedCover.InfoLinkModal.ButtonLabel"
                                        :align-right="false"
                                      />
                                    </template>
                                  </base-single-checkbox-card>
                                  <p
                                    v-if="
                                      nestedCover.DisplayedHelpTextOnSelected !== '' &&
                                      nestedCover.IsSelected
                                    "
                                    class="info-box is-open info-box-align-left no-margin-top"
                                    v-html="nestedCover.DisplayedHelpTextOnSelected"
                                  />
                                  <p
                                    v-if="
                                      nestedCover.DisplayedHelpTextOnDeSelected !== '' &&
                                      !nestedCover.IsSelected
                                    "
                                    class="info-box is-open info-box-align-left no-margin-top"
                                    v-html="nestedCover.DisplayedHelpTextOnDeSelected"
                                  />
                                  <p
                                    v-if="
                                      coverDisableStatus(nestedCover.GroupId, nestedCover.OptionsId)
                                    "
                                    class="info-box is-open info-box-align-left no-margin-top"
                                    v-html="
                                      coverDisableReason(nestedCover.GroupId, nestedCover.OptionsId)
                                    "
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- GROUPED -->
                        <div
                          v-if="
                            cover.RenderingType === 6 &&
                            cover.GroupedCovers &&
                            cover.GroupedCovers.length > 0
                          "
                          data-test-cover
                          data-test-cover-type="group"
                        >
                          <p>
                            {{ cover.Name }}
                            <tooltip
                              v-if="cover.HasTooltip"
                              :id="coverSectionIndex + '-cover-' + index + '-' + cover.OptionsId"
                                :content="cover.Tooltip"
                            />
                          </p>
                          <fieldset :id="'fieldSet' + cover.Name">
                            <div
                              v-for="(groupedCover, index) in cover.GroupedCovers"
                              :key="'cover-grouped-cover-' + index"
                              class="radio-inline"
                            >
                              <input
                                :id="'' + cover.Name + index"
                                v-model="cover.selected"
                                type="radio"
                                :name="cover.Name"
                                :value="groupedCover"
                                :data-test-quick-quote-covers-group-id="groupedCover.GroupId"
                                :data-test-quick-quote-covers-options-id="groupedCover.OptionsId"
                                @change="updatePriceGroupedCover(groupedCover, cover)"
                              />
                              <LabelAndTooltip
                                :id="'cover-' + index + '-' + groupedCover.OptionsId"
                                :for="'' + cover.Name + index"
                                :data-test-quick-quote-covers-group-option-value="groupedCover.Name"
                                :label="groupedCover.Name"
                                :tooltip="groupedCover.Tooltip"
                              />
                              <p
                                v-if="
                                  groupedCover.DisplayedHelpTextOnSelected !== '' &&
                                  groupedCover.IsSelected
                                "
                                class="info-box is-open info-box-align-left no-margin-top"
                                v-html="groupedCover.DisplayedHelpTextOnSelected"
                              />
                              <p
                                v-if="
                                  groupedCover.DisplayedHelpTextOnDeSelected !== '' &&
                                  !groupedCover.IsSelected
                                "
                                class="info-box is-open info-box-align-left no-margin-top"
                                v-html="groupedCover.DisplayedHelpTextOnDeSelected"
                              />
                              <p
                                v-if="
                                  coverDisableStatus(groupedCover.GroupId, groupedCover.OptionsId)
                                "
                                class="info-box is-open info-box-align-left no-margin-top"
                                v-html="
                                  coverDisableReason(groupedCover.GroupId, groupedCover.OptionsId)
                                "
                              />
                            </div>
                          </fieldset>
                        </div>
                        <!-- GROUPED CARD -->
                        <div
                          v-if="
                            cover.RenderingType === 9 &&
                            cover.GroupedCovers &&
                            cover.GroupedCovers.length > 0
                          "
                          data-test-cover
                          data-test-cover-type="group"
                        >
                          <p>
                            {{ cover.Name }}
                          </p>
                          <fieldset
                            v-for="(groupedCover, index) in cover.GroupedCovers"
                            :id="'fieldset' + cover.Name"
                            :key="'cover-grouped-cover-' + index"
                          >
                            <base-radio-button-single-card
                              :id="'base-radio-button-single-card-id-' + cover.Name + '-' + index"
                              v-model="cover.selected"
                              :option="{
                                label: groupedCover.Name,
                                description: groupedCover.Tooltip,
                                value: groupedCover
                              }"
                              :name="cover.Name"
                              @input="updatePriceGroupedCover(groupedCover, cover)"
                            >
                              <template #infolink>
                                <base-info-link-modal
                                  v-if="groupedCover.InfoLinkModal.HasInfoLinkModal"
                                  :link-label="groupedCover.InfoLinkModal.LinkLabel"
                                  :title="groupedCover.InfoLinkModal.Title"
                                  :content="groupedCover.InfoLinkModal.Content"
                                  :button-label="groupedCover.InfoLinkModal.ButtonLabel"
                                  :align-right="false"
                                />
                              </template>
                            </base-radio-button-single-card>
                            <p
                              v-if="
                                groupedCover.DisplayedHelpTextOnSelected !== '' &&
                                groupedCover.IsSelected
                              "
                              class="info-box is-open info-box-align-left no-margin-top"
                              v-html="groupedCover.DisplayedHelpTextOnSelected"
                            />
                            <p
                              v-if="
                                groupedCover.DisplayedHelpTextOnDeSelected !== '' &&
                                !groupedCover.IsSelected
                              "
                              class="info-box is-open info-box-align-left no-margin-top"
                              v-html="groupedCover.DisplayedHelpTextOnDeSelected"
                            />
                            <p
                              v-if="
                                coverDisableStatus(groupedCover.GroupId, groupedCover.OptionsId)
                              "
                              class="info-box is-open info-box-align-left no-margin-top"
                              v-html="
                                coverDisableReason(groupedCover.GroupId, groupedCover.OptionsId)
                              "
                            />
                          </fieldset>
                        </div>
                        <!-- COVER END -->
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <!-- COVER SECTION DIVIDER -->
              <hr
                v-if="
                  coverSectionIndex < vueModel.Coverage.CoverSections.length - 1 &&
                  !coverSection.UseAlternativeDesign
                "
              />
            </div>
          </div>
        </article>
      </section>
    </div>
    <section v-if="showDocuments" data-test-document-section class="qq-document-section">
      <hr />
      <a
        class="link-icon-left"
        :href="contentModel.SharedFields.TermsDocumentLink.Url"
        target="_blank"
        v-if="showTermsDocumentLink"
      >
        <span class="icon-download" />
        <span v-html="contentModel.SharedFields.TermsDocumentLink.Text" />
      </a>
      <br />
      <a
        class="link-icon-left"
        :href="contentModel.SharedFields.PidDocumentLink.Url"
        target="_blank"
        v-if="showPidDocumentLink"
      >
        <span class="icon-download" />
        <span v-html="contentModel.SharedFields.PidDocumentLink.Text" />
      </a>
      <hr />
    </section>
  </div>
</template>

<script>
import QuickQuoteMixin from './QuickQuoteMixin.vue';
import { hasProperty } from '@/common/Util/utility.js';
import Tooltip from '@/components/UI/Tooltip.vue';

import Utility from '@/common/UtilityFunctions.js';
import BaseSingleCheckboxCard from '@/components/Forms/BaseSingleCheckboxCard.vue';
import BaseRadioButtonSingleCard from '@/components/Forms/BaseRadioButtonSingleCard.vue';
import BaseInfoLinkModal from '@/components/Forms/BaseInfoLinkModal/BaseInfoLinkModal.vue';
import LabelAndInfoLinkModal from '@/components/UI/LabelAndInfoLinkModal.vue';
import axios from 'axios';
import vueSlider from 'vue-slider-component';
//just imported the default, the _slider.scss may be hardcoded to work with previous version of
//vue-slider-component, which is not supported for Vue3 (needs a new stylesheet to be pretty enough for cypress)
import 'vue-slider-component/theme/default.css';
import { cloneDeep, find, forEach } from 'lodash';

export default {
  name: 'Coverage',
  components: {
    vueSlider,
    Tooltip,

    BaseSingleCheckboxCard,
    BaseRadioButtonSingleCard,
    BaseInfoLinkModal,
    LabelAndInfoLinkModal
  },
  directives: {
    setRadioSelcted: {
      update(el, coverEl, vnode) {
        if (!vnode.context.updating) {
          Vue.set(
            coverEl.value,
            'selected',
            coverEl.value.Amounts.find((amount) => amount.IsSelected).Text
          );
        }
      }
    }
  },
  emits: ['update-covers'],
  mixins: [QuickQuoteMixin],
  props: {
    contentModel: {
      type: Object,
      default() {
        return {};
      }
    },
    vueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    stepModel: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      // TODO: Look at refactoring into class
      SelectedInsuranceAmount: '',
      InsuranceSumList: [],
      SelectedDeductibleAmount: '',
      dragging: false,
      coverModel: {
        PolicyCacheId: null,
        AmountUpdateType: null,
        AmountType: null,
        AmountRefId: null,
        CoverCacheId: null
      },
      updating: false,
      oldCoverage: {},
      oldCoversArrayForTagManager: []
    };
  },
  computed: {
    coversForTagManager() {
      const covers = new Object();
      forEach(this.vueModel.Coverage.CoverSections, (cover) => {
        if (!cover.CoverElements.length) {
          return;
        }
        if (
          cover.CoverElements.length === 1 &&
          cover.CoverElements[0].RenderingType !== 6 &&
          hasProperty(cover.CoverElements[0], 'selected')
        ) {
          if (cover.CoverElements[0].RenderingType < 2) {
            return;
          }
          const value = cover.CoverElements[0].selected;
          covers[this.cleanupCoverNameForTagManager(cover.Name)] = value;
        } else {
          forEach(cover.CoverElements, (coverElement) => {
            if (coverElement.RenderingType < 2) {
              return;
            }
            if (coverElement.RenderingType === 6) {
              const value = this.cleanupCoverNameForTagManager(
                coverElement.GroupedCovers.find((element) => element.IsSelected).Name
              );
              covers[
                this.cleanupCoverNameForTagManager(cover.Name) +
                  '-' +
                  this.cleanupCoverNameForTagManager(coverElement.Name)
              ] = value;
            } else {
              covers[
                this.cleanupCoverNameForTagManager(cover.Name) +
                  '-' +
                  this.cleanupCoverNameForTagManager(coverElement.Name)
              ] = coverElement.IsSelected ? 'ja' : 'nej';
            }
          });
        }
      });
      return covers;
    },
    coversArrayForTagManager() {
      const covers = [];
      forEach(this.coversForTagManager, (cover, index) => {
        covers.push([index, cover]);
      });
      return covers;
    },
    labelTitle() {
      let title = null;
      if (this.contentModel) {
        title =
          this.contentModel.SharedFields.LabelCoveragesTitle ??
          this.contentModel.SharedFields.LabelStep2;
      }
      return title;
    },
    showPidDocumentLink() {
      return this.contentModel?.SharedFields?.PidDocumentLink?.Url && this.contentModel?.SharedFields?.PidDocumentLink?.Text;
    },
    showTermsDocumentLink() {
      return this.contentModel?.SharedFields?.TermsDocumentLink?.Url && this.contentModel?.SharedFields?.TermsDocumentLink?.Text;
    },
    showDocuments() {
      return this.showPidDocumentLink || this.showTermsDocumentLink;
    },
  },
  watch: {
    'coverModel.PolicyCacheId': function (val) {
      if (val) {
        this.updating = true;
        axios
          .post(Utility.constants().API.quickquote_update_cover, this.coverModel)
          .then((response) => {
            if (response.data) {
              this.emitCovers(response);
            } else {
              this.showErrorModal();
            }
            this.resetCoverModel();
            this.saveOldCoverage();
            this.updating = false;
          })
          .catch((err) => {
            Utility.logError(err, this);
            this.showErrorModal();
            this.resetCoverModel();
            this.resetToOldCoverage();
            this.updating = false;
            this.pushToTagManagerOnError(err);
          });
      }
    },
    coversForTagManager() {
      this.$emit('update-covers', {
        event: 'track-event',
        eventCategory: this.getDanishProductTypeName + ' prisberegner',
        eventAction: '02 vælg dækning læg i kurv',
        eventLabel: '',
        eventValue: '0',
        eventNoninteraction: true,
        data: this.coversForTagManager
      });
    },
    'vueModel.Coverage.YearlyPrice': function () {
      window.setTimeout(() => this.pushToTagManageronChangeCoverage(), 200);
    },
    'vueModel.Coverage': function () {
      this.removeZeroDeductible();
    }
  },
  mounted() {
    this.removeZeroDeductible();
    this.saveOldCoverage();
    this.setSelectedGroupedCovers();
    this.setSlideStepInitValue();
    this.setSelectedDropDownValues();
    this.pushToTagManageronLoad();
    window.setTimeout(
      () => (this.oldCoversArrayForTagManager = this.coversArrayForTagManager),
      500
    );
  },
  methods: {
    removeZeroDeductible() {
      if (
        this.vueModel.ProductTypeName === 'House' &&
        this.vueModel.Coverage.CoverSections[0].CoverElements[0].Amounts[0].Text === '0 kr.'
      ) {
        this.vueModel.Coverage.CoverSections[0].CoverElements[0].Amounts.shift();
      }
    },
    coverDisableStatus(groupId, optionId) {
      var verificationIssues = this.vueModel.Coverage.ProductRuleVerificationErrors;
      if (verificationIssues === null || verificationIssues.length === 0) {
        return false;
      }

      for (var i = 0; i < verificationIssues.length; i++) {
        var issue = verificationIssues[i];
        if (issue.RuleType == 1 && issue.Type == 1) {
          if (groupId == issue.GroupId && optionId == issue.OptionId) {
            return true;
          }
        }
      }
      return false;
    },
    coverDisableReason(groupId, optionId) {
      var verificationIssues = this.vueModel.Coverage.ProductRuleVerificationErrors;
      if (verificationIssues === null || verificationIssues.length === 0) {
        return false;
      }

      for (var i = 0; i < verificationIssues.length; i++) {
        var issue = verificationIssues[i];
        if (issue.RuleType == 1 && issue.Type == 1) {
          if (groupId == issue.GroupId && optionId == issue.OptionId) {
            return issue.Text;
          }
        }
      }
      return null;
    },
    isStaticCoverWithValue(cover) {
      return cover.RenderingType === 1 && cover.Value;
    },
    saveOldCoverage() {
      this.oldCoverage = cloneDeep(this.vueModel.Coverage);
    },
    resetToOldCoverage() {
      this.vueModel.Coverage = cloneDeep(this.oldCoverage);
      this.showErrorModal();
      this.setSelectedGroupedCovers();
      this.setSlideStepInitValue();
      this.setSelectedDropDownValues();
    },
    resetCoverModel() {
      this.coverModel = {
        PolicyCacheId: null,
        AmountUpdateType: null,
        AmountType: null,
        AmountRefId: null,
        CoverCacheId: null
      };
    },
    getSlideTexts(slideAmounts) {
      return slideAmounts.map((ob) => ob.Text);
    },
    getSlideIds(slideAmounts) {
      return slideAmounts.map((ob) => ob.Id);
    },
    updatePriceCover(cover) {
      this.updateSingleCover(cover);
    },
    updatePriceGroupedCover(groupedCovers, cover) {
      let url = Utility.constants().API.quickquote_changegroupedcover;
      let data = {
        PolicyCacheId: this.vueModel.PolicyCacheId,
        Id: groupedCovers.Id,
        ProductLineGroupRefId: groupedCovers.GroupId,
        ProductLineOptionRefId: groupedCovers.OptionsId
      };
      if (
        hasProperty(cover, 'oldSelected') &&
        (cover.oldSelected.GroupId === '' || cover.oldSelected.GroupId === 0)
      ) {
        url = Utility.constants().API.quickquote_add_cover;
      }
      if (
        hasProperty(cover, 'oldSelected') &&
        (groupedCovers.GroupId === '' || groupedCovers.GroupId === 0)
      ) {
        url = Utility.constants().API.quickquote_remove_cover;
        data = {
          PolicyCacheId: this.vueModel.PolicyCacheId,
          Id: cover.oldSelected.Id,
          ProductLineGroupRefId: cover.oldSelected.GroupId,
          ProductLineOptionRefId: cover.oldSelected.OptionsId
        };
      }

      return axios
        .post(url, data)
        .then((response) => {
          this.emitCovers(response);
          cover.oldSelected = groupedCovers;
          this.saveOldCoverage();
        })
        .catch((err) => {
          Utility.logError(err, this);
          this.resetToOldCoverage();
          this.pushToTagManagerOnError(err);
        });
    },
    deselctRest(nextCover, rest) {
      this.updateSingelCover(nextCover, () => {
        if (rest.length > 0) {
          this.deselect(rest);
        }
      });
    },
    deselect(rest) {
      const next = rest.pop();
      next.IsSelected = false;
      this.deselctRest(next, rest);
    },
    updateSingleCover(cover, callbackDone = null) {
      this.postCover(cover)
        .then((response) => {
          this.emitCovers(response);
          if (callbackDone) {
            callbackDone();
          }
          this.saveOldCoverage();
        })
        .catch((err) => {
          Utility.logError(err, this);
          this.resetToOldCoverage();
          this.pushToTagManagerOnError(err);
        });
    },
    postCover(cover) {
      let url = Utility.constants().API.quickquote_add_cover;
      if (!cover.IsSelected) {
        url = Utility.constants().API.quickquote_remove_cover;
      }
      var data = {
        PolicyCacheId: this.vueModel.PolicyCacheId,
        Id: cover.Id,
        ProductLineGroupRefId: cover.GroupId,
        ProductLineOptionRefId: cover.OptionsId
      };
      return axios.post(url, data);
    },
    updateBackend(url, data) {
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };
      return axios.post(url, JSON.stringify(data), config);
    },
    emitCovers(response) {
      // this.$parent.$emit('updateCovers', response.data);
      this.$parent.$parent.$parent.updateCovers(response.data);
      this.setSelectedGroupedCovers();
      this.setSelectedDropDownValues();
      this.setSlideStepInitValue();
    },
    findSelected(groupedCovers) {
      return find(groupedCovers, (cover) => cover.IsSelected);
    },
    setSelectedGroupedCovers() {
      forEach(this.vueModel.Coverage.CoverSections, (coverSection) => {
        forEach(coverSection.CoverElements, (coverGroup) => {
          if (hasProperty(coverGroup, 'GroupedCovers')) {
            const foundSelected = this.findSelected(coverGroup.GroupedCovers);
            coverGroup.oldSelected = coverGroup.selected ? coverGroup.selected : foundSelected;
            // https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
            // this.$set(coverGroup, 'selected', foundSelected);
            coverGroup.selected = foundSelected;
          }
        });
      });
    },
    setSlideValue(cover) {
      if (!this.dragging) {
        this.setValue(cover);
      }
    },
    setSlideStepInitValue() {
      forEach(this.vueModel.Coverage.CoverSections, (coverSection) => {
        var coverEl = coverSection.CoverElements[0];
        if (coverEl.RenderingType === 2) {
          var selectedAmount = coverEl.Amounts.find((item) => item.IsSelected === true);
          if (typeof selectedAmount !== 'undefined') {
            coverEl.selected = selectedAmount.Text;
          }
        }
      });
    },
    setSelectedDropDownValues() {
      forEach(this.vueModel.Coverage.CoverSections, (coverSection) => {
        forEach(coverSection.CoverElements, (coverEl) => {
          if (coverEl.RenderingType === 3) {
            var selectedAmount = coverEl.Amounts.find((item) => item.IsSelected === true);
            if (typeof selectedAmount !== 'undefined') {
              coverEl.selected = selectedAmount;
            }
          }
        });
      });
    },
    setValue(cover) {
      const selected = cover.Amounts.find((item) => item.Text === cover.selected);
      this.setCoverModel(cover, selected.Id);
    },
    setValueFromObject(cover) {
      const selected = cover.Amounts.find((item) => item.Text === cover.selected.Text);
      this.setCoverModel(cover, selected.Id);
    },
    setCoverModel(cover, amountRefId) {
      this.coverModel = {
        PolicyCacheId: this.vueModel.PolicyCacheId,
        AmountUpdateType: cover.AmountUpdateType,
        AmountType: cover.AmountType,
        AmountRefId: amountRefId,
        CoverCacheId: cover.Id
      };
    },
    // For Tag Manager tracking:
    pushToTagManageronLoad() {
      Utility.pushToTagManager({
        event: 'track-ga-vp',
        virtualPath: '/virtual/' + this.getDanishProductTypeName + '-prisberegner-02-vælg-dækning',
        virtualTitle:
          'Virtuel - Beregn pris - ' +
          this.getDanishProductTypeName.charAt(0).toUpperCase() +
          this.getDanishProductTypeName.substring(1) +
          ' - Vælg dækning'
      });
    },
    pushToTagManageronChangeCoverage() {
      for (var i = 0; i < this.oldCoversArrayForTagManager.length; i++) {
        const cover = this.coversArrayForTagManager.find(
          (element) => element[0] === this.oldCoversArrayForTagManager[i][0]
        );
        if (typeof cover === 'undefined') {
          continue;
        }

        if (
          typeof cover[1] === 'object' &&
          cover[1].Id === this.oldCoversArrayForTagManager[i][1].Id
        ) {
          continue;
        }

        if (cover[1] !== this.oldCoversArrayForTagManager[i][1]) {
          Utility.pushToTagManager({
            event: 'track-event',
            eventCategory: this.getDanishProductTypeName + ' prisberegner',
            eventAction: '02 vælg dækning - ' + cover[0],
            eventLabel:
              typeof cover[1] === 'object' && typeof cover[1].Text !== 'undefined'
                ? this.cleanupCoverNameForTagManager(cover[1].Text)
                : this.cleanupCoverNameForTagManager(cover[1]),
            eventValue: '0',
            eventNoninteraction: true
          });
          this.oldCoversArrayForTagManager[i][1] = cover[1];
        }
      }
    },
    pushToTagManagerOnError(errorMessage) {
      Utility.pushToTagManager({
        event: 'track-event',
        eventCategory: this.getDanishProductTypeName + ' prisberegner',
        eventAction: '02 vælg dækning error',
        eventLabel: errorMessage,
        eventValue: '0',
        eventNoninteraction: true
      });
    },
    cleanupCoverNameForTagManager(str) {
      const stringWithoutParentheses = str.split(' (')[0];
      let trimmedString = stringWithoutParentheses.trim();
      const lastCharacter = trimmedString[[trimmedString.length - 1]];
      if (lastCharacter === '?' || lastCharacter === '!' || lastCharacter === '.') {
        trimmedString = trimmedString.slice(0, -1);
      }
      const finalString = trimmedString
        .toLowerCase()
        .replace(/ /g, '-')
        .replace('--', '-')
        .replace(/&#(\d+);/g, (m, n) => String.fromCharCode(n));
      return finalString;
    },
    removeSpecialChars(str) {
      const finalString = str.replace(/[\s]|[#]|[&]|[;]/g, '');
      return finalString;
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/styles/all.scss';

.qq-alternative-design {
  background: $background-light;
  padding: rem-calc(24);
  border-radius: rem-calc(15);

  .qq-p-header {
    width: auto;
    border-bottom: rem-calc(2) $black solid;

    strong {
      &.h2 {
        color: $black;
      }
    }
  }

  .tooltip {
    display: none;

    .tooltip-icon {
      display: none;

      .tooltip-bubble {
        display: none;

        p {
          display: none;
          border-bottom: none;
        }
      }
    }
  }

  .list-check-icon {
    li {
      padding-left: 0;
    }

    li:before {
      display: none;
    }
  }
}

.qq-document-section {
  margin-top: rem-calc(-48);
}
</style>
